import { Mic, Brain, Calendar, Smartphone, Link2, MessageCircle, Shield } from 'lucide-react'
import Section, { SectionHeader } from '../components/Section'
import Button from '../components/Button'
import { FeatureCard } from '../components/Card'

export default function HowItWorks() {
    return (
        <>
            {/* Hero Section */}
            <Section className="pt-12 lg:pt-20">
                <div className="text-center max-w-4xl mx-auto">
                    <h1 className="text-4xl lg:text-5xl font-bold text-primary-color leading-tight mb-6">
                        From Thought to Done in Seconds
                    </h1>
                    <p className="text-lg lg:text-xl font-normal text-secondary-color leading-relaxed">
                        See how Way2Will captures your ideas through natural conversation and transforms them into organized action.
                    </p>
                </div>
            </Section>

            {/* Workflow Visualization Section */}
            <Section glass>
                <SectionHeader title="The Three-Second Workflow" />

                <div className="flex flex-col lg:flex-row gap-8 lg:gap-4">
                    {/* Step 1 */}
                    <div className="flex-1 glass-prominent p-8 rounded-2xl">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-12 h-12 rounded-full gradient-primary flex items-center justify-center">
                                <span className="text-xl font-bold text-white/85">1</span>
                            </div>
                            <h3 className="text-xl font-semibold text-primary-color">You Speak</h3>
                        </div>
                        <div className="flex flex-col gap-4">
                            <div className="w-16 h-16 mx-auto rounded-full gradient-primary flex items-center justify-center mb-4">
                                <Mic className="w-8 h-8 text-white/85" />
                            </div>
                            <h4 className="text-lg font-semibold text-primary-color text-center">Capture Anywhere, Anytime</h4>
                            <p className="text-base text-secondary-color text-center">
                                Press the Walkie-Talkie button from your lock screen or desktop. Speak naturally about tasks, meetings, ideas, or feelings.
                            </p>
                            <div className="glass p-4 rounded-lg mt-4">
                                <p className="text-sm italic text-secondary-color">
                                    "Remind me to call John on Tuesday about the Q3 report, and I'm feeling stressed about the marketing launch."
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Step 2 */}
                    <div className="flex-1 glass-prominent p-8 rounded-2xl">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-12 h-12 rounded-full gradient-primary flex items-center justify-center">
                                <span className="text-xl font-bold text-white/85">2</span>
                            </div>
                            <h3 className="text-xl font-semibold text-primary-color">AI Thinks</h3>
                        </div>
                        <div className="flex flex-col gap-4">
                            <div className="w-16 h-16 mx-auto rounded-full gradient-primary flex items-center justify-center mb-4">
                                <Brain className="w-8 h-8 text-white/85" />
                            </div>
                            <h4 className="text-lg font-semibold text-primary-color text-center">Instant Intelligence</h4>
                            <p className="text-base text-secondary-color text-center">
                                Way2Will understands context, extracts intent, and categorizes information in under 800ms.
                            </p>
                            <div className="flex flex-col gap-2 mt-4">
                                <div className="glass p-3 rounded-lg">
                                    <span className="text-xs text-primary-base font-semibold">Task Extracted</span>
                                    <p className="text-sm text-secondary-color">Call John re: Q3 Report (Due: Tuesday)</p>
                                </div>
                                <div className="glass p-3 rounded-lg">
                                    <span className="text-xs text-primary-base font-semibold">Journal Entry</span>
                                    <p className="text-sm text-secondary-color">Stress regarding Marketing Launch</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Step 3 */}
                    <div className="flex-1 glass-prominent p-8 rounded-2xl">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-12 h-12 rounded-full gradient-primary flex items-center justify-center">
                                <span className="text-xl font-bold text-white/85">3</span>
                            </div>
                            <h3 className="text-xl font-semibold text-primary-color">AI Acts</h3>
                        </div>
                        <div className="flex flex-col gap-4">
                            <div className="w-16 h-16 mx-auto rounded-full gradient-primary flex items-center justify-center mb-4">
                                <Calendar className="w-8 h-8 text-white/85" />
                            </div>
                            <h4 className="text-lg font-semibold text-primary-color text-center">Automatic Organization</h4>
                            <p className="text-base text-secondary-color text-center">
                                Tasks are scheduled, meetings are blocked, and follow-ups are set. The AI negotiates with you if plans change.
                            </p>
                            <div className="glass p-4 rounded-lg mt-4">
                                <div className="flex flex-col gap-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-primary-base"></div>
                                        <span className="text-sm text-secondary-color">Tuesday 10:00 AM - Call John</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-accent-cyan"></div>
                                        <span className="text-sm text-secondary-color">Friday 2:00 PM - Marketing Review</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            {/* Platform Features Section */}
            <Section>
                <SectionHeader title="Built for Your Workflow" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    <FeatureCard
                        icon={<Smartphone className="w-full h-full" />}
                        title="Cross-Platform Capture"
                        description="Access from iOS, Android, Mac, Windows, or web. Lock screen widget keeps capture one tap away."
                    />
                    <FeatureCard
                        icon={<Link2 className="w-full h-full" />}
                        title="Seamless Integration"
                        description="Two-way sync with Google Calendar, Outlook, Notion, Slack, and Gmail."
                    />
                    <FeatureCard
                        icon={<MessageCircle className="w-full h-full" />}
                        title="Natural Conversation"
                        description="Interrupt anytime with barge-in capability. Feels like talking to a human assistant."
                    />
                    <FeatureCard
                        icon={<Shield className="w-full h-full" />}
                        title="Privacy First"
                        description="GDPR and BIPA compliant. Your voice data is encrypted and never used to train third-party models."
                    />
                </div>
            </Section>

            {/* Comparison Section */}
            <Section glass>
                <SectionHeader title="Why Way2Will Beats the Rest" />
                <div className="overflow-x-auto">
                    <table className="w-full glass rounded-2xl overflow-hidden">
                        <thead>
                            <tr className="border-b border-primary-base/10">
                                <th className="p-4 text-left text-primary-color font-semibold">Feature</th>
                                <th className="p-4 text-center text-primary-base font-semibold">Way2Will</th>
                                <th className="p-4 text-center text-secondary-color font-semibold">Motion</th>
                                <th className="p-4 text-center text-secondary-color font-semibold">Sunsama</th>
                                <th className="p-4 text-center text-secondary-color font-semibold">Notion</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                { feature: 'Voice-First Capture', w2w: true, motion: false, sunsama: false, notion: false },
                                { feature: 'Auto-Scheduling', w2w: true, motion: true, sunsama: false, notion: false },
                                { feature: 'Conversational AI', w2w: true, motion: false, sunsama: false, notion: false },
                                { feature: 'Long-Term Memory', w2w: true, motion: false, sunsama: false, notion: false },
                                { feature: 'Flexible Planning', w2w: true, motion: false, sunsama: true, notion: true },
                            ].map((row, index) => (
                                <tr key={index} className="border-b border-primary-base/5">
                                    <td className="p-4 text-secondary-color">{row.feature}</td>
                                    <td className="p-4 text-center">{row.w2w ? '✓' : '✗'}</td>
                                    <td className="p-4 text-center text-tertiary-color">{row.motion ? '✓' : '✗'}</td>
                                    <td className="p-4 text-center text-tertiary-color">{row.sunsama ? '✓' : '✗'}</td>
                                    <td className="p-4 text-center text-tertiary-color">{row.notion ? '✓' : '✗'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className="text-center text-secondary-color mt-8">
                    Stop fighting rigid algorithms or maintaining complex databases. Let AI do the work.
                </p>
            </Section>

            {/* CTA Section */}
            <Section>
                <div className="max-w-4xl mx-auto text-center">
                    <div className="glass-prominent p-12 lg:p-16 rounded-2xl">
                        <h2 className="text-3xl lg:text-4xl font-semibold text-primary-color mb-6">
                            Ready to Experience Antigravity?
                        </h2>
                        <p className="text-lg text-secondary-color mb-8">
                            Join professionals who've replaced their executive assistants with Way2Will.
                        </p>
                        <Button to="/signup" size="lg">
                            Start Your 14-Day Free Trial
                        </Button>
                        <p className="text-sm text-tertiary-color mt-6">
                            Full access • No credit card required • Cancel anytime
                        </p>
                    </div>
                </div>
            </Section>
        </>
    )
}
